import { qs, vaultFetch } from './vault-api-core.ts'
import type { VaultPrincipalKind } from './vault-api-principals.ts'

/**
 * The OPERATOR plane of vault messaging:
 * `/vault/admin/{rootId}/messages`, session cookie plus root membership.
 *
 * The agent plane (`/vault/v1/messages`, pass-authenticated) is the
 * daemon's and the CLI's; nothing in this app may call it, because an
 * agent pass is not something a browser holds and an operator cookie is
 * not something an agent may borrow (STO-24).
 *
 * CONFIDENTIALITY, STATED ONCE AND STATED HONESTLY. A body is sealed to
 * the TENANT public key, so ANY APPROVED DAEMON OF THIS TENANT can open
 * ANY of this tenant's messages. The recipient scoping the server applies
 * is AUTHORISATION, not cryptography. This is NOT end-to-end encrypted
 * between two principals — per-principal keypairs are the acknowledged
 * follow-up (D2), and principals have no keypair today. Rotating the
 * tenant key makes every existing message unreadable, because messages
 * are deliberately outside the rotation re-wrap set (D1).
 *
 * A LIST NEVER CARRIES A BODY. `VaultMessageSummary` has no `sealedBody`
 * and `VaultMessageFull` does; the split is in the wire contract and is
 * mirrored here so a screen that renders a list cannot accidentally hold
 * ciphertext it has no reason to.
 */

/** Either end of a message. `name` is `null` when the principal has since
 * been soft-deleted — the message outlives it, and an id with no
 * explanation is worse than an honest blank. */
export interface VaultMessageParticipant {
  principalId: string
  name: string | null
}

export interface VaultMessageSummary {
  id: string
  /** Messages in one conversation share this. A message that started a
   * thread has `threadId === id`. */
  threadId: string
  /**
   * PLAINTEXT, so an inbox is listable without the tenant key.
   *
   * UNTRUSTED TEXT A SENDER WROTE — and on the agent plane the sender may
   * be an agent. Render it as a string child so React escapes it; never
   * into `dangerouslySetInnerHTML`, a `title`, an `href` or a `style`.
   */
  subject: string
  sender: VaultMessageParticipant
  /** The sender's principal kind AS IT WAS AT SEND TIME, denormalised by
   * the server so it stays true after a principal changes. This is the
   * direction flag the thread view colours by. */
  senderKind: VaultPrincipalKind
  recipient: VaultMessageParticipant
  sessionId: string | null
  /** Which tenant key sealed the body. A body sealed under an older
   * version cannot be opened by the current key; the UI says so rather
   * than reporting a crypto failure. */
  kekVersion: number | null
  createdAt: string
  readAt: string | null
  consumedAt: string | null
  [key: string]: unknown
}

/** A summary plus the ciphertext. Only a by-id read returns one. */
export interface VaultMessageFull extends VaultMessageSummary {
  /** base64url of a sealed box to the tenant public key. Never logged,
   * never put in an error message. */
  sealedBody: string
}

export interface ListVaultMessagesOptions {
  /** 1..200; the server defaults to 50. */
  limit?: number
  /** A message id from the previous page's oldest row (uuidv7 cursor). */
  before?: string
  /** Narrows to messages this principal sent OR received. */
  principalId?: string
  /** Narrows to one conversation. The server returns a thread
   * OLDEST-first and ignores `before`/`includeConsumed` for it. */
  threadId?: string
  /** The operator plane defaults this to TRUE — an audit of
   * correspondence that hid the handled half would be a worse answer
   * than a longer list. Pass `false` for a working inbox. */
  includeConsumed?: boolean
}

/** One page of messages. Newest first for the root list, oldest first
 * when `threadId` is given — the server decides, and the caller must not
 * re-sort without knowing which it asked for. */
export async function listVaultMessages(
  rootId: string,
  options: ListVaultMessagesOptions = {},
): Promise<VaultMessageSummary[]> {
  const { messages } = await vaultFetch<{ messages: VaultMessageSummary[] }>(
    `/vault/admin/${rootId}/messages${qs({
      limit: options.limit?.toString(),
      before: options.before,
      principalId: options.principalId,
      threadId: options.threadId,
      includeConsumed: options.includeConsumed?.toString(),
    })}`,
  )
  return messages
}

/** One message WITH its sealed body, root-scoped. Opening it still needs
 * the tenant key unlocked in this browser — see `vault-message-crypto`. */
export function getVaultMessage(
  rootId: string,
  id: string,
): Promise<VaultMessageFull> {
  return vaultFetch<VaultMessageFull>(`/vault/admin/${rootId}/messages/${id}`)
}

export interface SendVaultMessageBody {
  /** The operator console HAS the principal directory, so it addresses by
   * id. (The agent plane addresses by name, because an agent does not.) */
  recipientPrincipalId: string
  subject?: string
  /** Continues an existing thread; omit to start a new one. */
  threadId?: string
  /** base64url sealed box, built by `sealMessageBody`. */
  sealedBody: string
}

/** Sends as the session user's own human principal. There is no sender
 * field and there must never be one: the server takes the sender from the
 * cookie, so there is nothing here to forge. */
export function sendVaultMessage(
  rootId: string,
  body: SendVaultMessageBody,
): Promise<VaultMessageFull> {
  return vaultFetch<VaultMessageFull>(`/vault/admin/${rootId}/messages`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Stamps `readAt`, and only on a message addressed to the caller's OWN
 * principal — read is the recipient's fact, and the server 404s anything
 * else. Idempotent: the first call stamps, later ones return the row.
 *
 * There is deliberately no operator `consume`. Consuming is how an agent
 * takes a message out of its working inbox; an operator clearing an
 * agent's queue from the console would hide work the agent has not seen.
 */
export function markVaultMessageRead(
  rootId: string,
  id: string,
): Promise<VaultMessageFull> {
  return vaultFetch<VaultMessageFull>(
    `/vault/admin/${rootId}/messages/${id}/read`,
    { method: 'POST' },
  )
}
